import type { UIMessage } from "ai";
import { Fragment, type FC, type ReactNode } from "react";
import type { ChatToolStatus, ToolComponent } from "./chatToolCall.js";
import { MessageTypingIndicator } from "./messageTypingIndicator.js";

type AnyUIMessagePart = UIMessage["parts"][number];

/**
 * Tool calls the agent runtime makes for its own bookkeeping, hidden on every
 * chat surface.
 */
const HIDDEN_TOOL_NAMES: ReadonlySet<string> = new Set(["updateWorkingMemory"]);

/** Tool renderers keyed by tool name, with a fallback for unmapped tools. */
export interface ChatToolComponents {
  byName?: Record<string, ToolComponent>;
  Fallback?: ToolComponent;
}

export interface ChatMessageRenderTextArgs {
  text: string;
  role: UIMessage["role"];
  /** ID of the message this text part belongs to (stable React keys, etc.). */
  messageId: string;
  /** True while this text part is still streaming in. */
  isStreaming: boolean;
}

export interface ChatMessageRenderReasoningArgs {
  text: string;
  isStreaming: boolean;
}

export interface ChatMessageProps<TMessage extends UIMessage = UIMessage> {
  message: TMessage;
  /** True when this message is the one currently being streamed by the agent. */
  isStreaming?: boolean;
  className?: string;
  tools?: ChatToolComponents;
  /** Renders a text part. Features supply the bubble + markdown styling. */
  renderText: (args: ChatMessageRenderTextArgs) => ReactNode;
  /**
   * Renders a reasoning part. Defaults to a typing indicator while streaming
   * and nothing once done (matching the previous assistant-ui behavior).
   */
  renderReasoning?: (args: ChatMessageRenderReasoningArgs) => ReactNode;
  /**
   * Shown while the message is streaming but no part is actively producing
   * content (e.g. between a finished tool call and the next text). Defaults to
   * a bare typing indicator.
   */
  renderTypingIndicator?: () => ReactNode;
}

/**
 * Renders a single AI SDK `UIMessage` by iterating its parts and dispatching
 * each to a renderer. Replaces the per-feature `UserMessage`/`AssistantMessage`
 * + `MessagePrimitive.Parts` boilerplate that every chat surface used to
 * duplicate against assistant-ui primitives.
 */
export function ChatMessage<TMessage extends UIMessage>(
  props: ChatMessageProps<TMessage>,
) {
  const { message, isStreaming = false, tools } = props;

  // Hidden tool parts are invisible to the indicator logic below as well: a
  // running one must not suppress the typing indicator.
  const parts = message.parts.filter((part) => !isHiddenToolPart(part));

  const anyPartActive = parts.some(isPartActive);

  // The flash: when the agent's final text part settles (state flips from
  // "streaming" to "done") the part goes inactive a beat before the message
  // stream actually closes, so `isStreaming` is briefly still true with nothing
  // active — long enough to mount the trailing indicator and yank the layout.
  // We only want that indicator while waiting on *new* content, which always
  // arrives as a fresh part: either there are no parts yet (just submitted) or
  // the last part is a non-text part (e.g. a finished tool call before the next
  // text). A settled trailing text part means the message is wrapping up, so
  // suppress it there.
  const lastPart = parts.at(-1);
  const endsWithSettledText =
    lastPart?.type === "text" && lastPart.state !== "streaming";

  return (
    <>
      {parts.map((part, index) => {
        const key = `${message.id}-${index}`;

        if (part.type === "text") {
          // While streaming, skip an empty text part — the trailing typing
          // indicator stands in for it (avoids a flashing empty bubble).
          if (isStreaming && part.text.trim() === "") {
            return null;
          }
          return (
            <Fragment key={key}>
              {props.renderText({
                text: part.text,
                role: message.role,
                messageId: message.id,
                isStreaming: part.state === "streaming",
              })}
            </Fragment>
          );
        }

        if (part.type === "reasoning") {
          const reasoningIsStreaming = part.state === "streaming";
          if (props.renderReasoning) {
            return (
              <Fragment key={key}>
                {props.renderReasoning({
                  text: part.text,
                  isStreaming: reasoningIsStreaming,
                })}
              </Fragment>
            );
          }
          return reasoningIsStreaming ? (
            <DefaultTypingIndicator key={key} />
          ) : null;
        }

        const toolPart = getToolPart(part);
        if (toolPart) {
          const Component =
            tools?.byName?.[toolPart.toolName] ?? tools?.Fallback;
          if (!Component) {
            return null;
          }
          return <Component key={key} {...toolPart} />;
        }

        // source-url / source-document / file / data-* / step-start are not
        // rendered by these chat surfaces today.
        return null;
      })}

      {isStreaming && !anyPartActive && !endsWithSettledText
        ? (props.renderTypingIndicator?.() ?? <DefaultTypingIndicator />)
        : null}
    </>
  );
}

/**
 * Default typing indicator. Pads the dots with `py-2` so they keep the same
 * vertical rhythm as the message bubbles (which are `py-2`) instead of butting
 * up against the message above — matching the previous assistant-ui behavior,
 * where the indicator lived inside an assistant bubble. Features that render
 * their own bubble can override this via `renderTypingIndicator`.
 */
const DefaultTypingIndicator: FC = () => (
  <div className="py-2">
    <MessageTypingIndicator />
  </div>
);

interface NormalizedToolPart {
  toolName: string;
  toolCallId: string;
  input: unknown;
  output: unknown;
  errorText: string | undefined;
  status: ChatToolStatus;
}

/** Normalize an AI SDK tool part (`tool-${name}` or `dynamic-tool`). */
function getToolPart(part: AnyUIMessagePart): NormalizedToolPart | null {
  const toolName =
    part.type === "dynamic-tool"
      ? part.toolName
      : part.type.startsWith("tool-")
        ? part.type.slice("tool-".length)
        : null;

  if (toolName === null) {
    return null;
  }

  // All tool parts share these fields across their state union.
  const toolPart = part as Extract<AnyUIMessagePart, { toolCallId: string }> & {
    state: string;
    input?: unknown;
    output?: unknown;
    errorText?: string;
  };

  return {
    toolName,
    toolCallId: toolPart.toolCallId,
    // While the input streams in, `input` holds the partially parsed object
    // (or nothing at all yet). Default it to an object so tool components'
    // flat field reads (`input.title`) see `undefined` fields, not a crash —
    // letting them show their friendly running label from the first chunk.
    input: toolPart.input ?? {},
    output: toolPart.output,
    errorText: toolPart.errorText,
    status: toolStatus(toolPart.state),
  };
}

function toolStatus(state: string): ChatToolStatus {
  if (state === "output-available") {
    return { type: "complete" };
  }
  if (state === "output-error" || state === "output-denied") {
    return { type: "incomplete" };
  }
  return { type: "running" };
}

function isHiddenToolPart(part: AnyUIMessagePart): boolean {
  const toolPart = getToolPart(part);
  return toolPart !== null && HIDDEN_TOOL_NAMES.has(toolPart.toolName);
}

/** Whether a part is actively producing content (drives the typing indicator). */
function isPartActive(part: AnyUIMessagePart): boolean {
  if (part.type === "text" || part.type === "reasoning") {
    return part.state === "streaming";
  }
  const toolPart = getToolPart(part);
  return toolPart?.status.type === "running";
}
